"use client";

import {
  Zap,
  Users,
  Calculator,
  Building2,
  Globe,
  BookOpen,
  Megaphone,
} from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";

const features = [
  {
    icon: <Users size={18} />,
    title: "Get Started",
    description:
      "Begin your business journey with expert guidance and a smooth, hassle-free process.",
    href: "/contact?source=get-started&message=I%20want%20to%20get%20started",
  },
  {
    icon: <Calculator size={18} />,
    title: "Calculate Cost",
    description:
      "Get an instant estimate tailored to your business setup requirements in the UAE.",
    href: "/calculator",
  },
  {
    icon: <Zap size={18} />,
    title: "Talk to an Expert",
    description:
      "Connect with our specialists for clear advice and the right solution for your needs.",
    href: "https://wa.me/971507987470?text=Hello%20I%20need%20expert%20advice%20about%20business%20setup",
  },
];

const specialisms = [
  { icon: <Building2 size={16} />, label: "Company Formation" },
  { icon: <Globe size={16} />, label: "UAE & Global Visa Services" },
  { icon: <BookOpen size={16} />, label: "Accounting & Tax" },
  { icon: <Megaphone size={16} />, label: "Digital Marketing" },
];

export default function StartSmart() {
  return (
    <section className="relative py-12 sm:py-16 lg:py-20 xl:py-24 px-4 md:px-8 bg-[#FAFAFA] overflow-hidden">
      <div className="main-container">
        {/* Top Section */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-14 sm:mb-18 lg:mb-20">
          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#B8893A] font-semibold">
              Your trusted partner for business setup and growth in the UAE
            </p>

            <h2
              className="font-bold text-[#000000]"
              style={{
                fontSize: "clamp(28px, 5vw, 48px)",
                lineHeight: "1.2",
              }}
            >
              Start Smart,{" "}
              <span className="font-serif font-normal text-[#B8893A]">
                Grow Strong
              </span>
            </h2>

            <p
              className="text-gray-500"
              style={{
                fontSize: "clamp(14px, 1.2vw, 16px)",
                lineHeight: "1.6",
              }}
            >
              AWS Businessman is your one-stop business and corporate services
              partner in the UAE making every step of your journey smooth,
              efficient, and hassle-free.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap gap-3 pt-1">
              <Link
                href="/contact"
                className="px-6 py-3 rounded-xl bg-[#B8893A] text-white font-bold shadow hover:bg-[#A67B34] transition-all hover:scale-[1.03] text-sm sm:text-base"
              >
                Book Free Consultation
              </Link>

              <Link
                href="/calculator"
                className="px-6 py-3 rounded-xl border-2 border-[#B8893A] text-[#B8893A] font-bold hover:bg-[#B8893A]/5 transition-all text-sm sm:text-base"
              >
                Get Instant Quote
              </Link>
            </div>

            {/* Services Below Buttons */}
            <div className="pt-6">
              <div className="flex flex-wrap gap-3">
                {specialisms.map((s) => (
                  <div
                    key={s.label}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all"
                  >
                    <span className="text-[#B8893A]">{s.icon}</span>
                    <span className="text-sm font-medium text-gray-700">
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* RIGHT IMAGE ONLY */}
        {/* RIGHT IMAGE WITH LOGO */}
{/* RIGHT IMAGE WITH TOP RIGHT LOGO */}
<motion.div
  initial={{ opacity: 0, x: 24 }}
  whileInView={{ opacity: 1, x: 0 }}
  viewport={{ once: true }}
  className="relative overflow-hidden rounded-3xl shadow-2xl border border-gray-100 h-[320px] sm:h-[380px] lg:h-[420px]"
>
  {/* Background Image */}
  <img
    src="/aws.webp"
    alt="AWS Business Environment"
    className="w-full h-full object-cover"
  />

  {/* Overlay */}
  {/* <div className="absolute inset-0 bg-black/25" /> */}

  {/* Logo Top Right */}
  <div className="absolute top-5 right-5 z-10">
    <img
      src="/white-text-logo-png.png"
      alt="AWS Logo"
      className="w-[100px] sm:w-[130px] lg:w-[160px] h-auto "
    />
  </div>
</motion.div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              {f.href && f.href.startsWith("http") ? (
                <a
                  href={f.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 lg:p-7 shadow-md hover:shadow-xl hover:border-[#B8893A]/30 transition-all duration-300 flex flex-col justify-between h-full"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3
                      className="font-semibold text-black leading-snug
                    text-base sm:text-lg lg:text-xl
                    group-hover:text-[#B8893A] transition"
                    >
                      {f.title}
                    </h3>

                    <div className="text-black/40 group-hover:text-[#B8893A] transition mt-1">
                      {f.icon}
                    </div>
                  </div>

                  <div className="h-px bg-black/10 my-5 sm:my-6"></div>

                  <p
                    className="text-gray-500"
                    style={{
                      fontSize: "clamp(14px, 1.2vw, 16px)",
                      lineHeight: "1.6",
                    }}
                  >
                    {f.description}
                  </p>

                  <div className="mt-6 text-[#B8893A] text-sm font-semibold">
                    Learn More →
                  </div>
                </a>
              ) : (
                <Link
                  href={f.href}
                  className="group bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 lg:p-7 shadow-md hover:shadow-xl hover:border-[#B8893A]/30 transition-all duration-300 flex flex-col justify-between h-full"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3
                      className="font-semibold text-black leading-snug
                    text-base sm:text-lg lg:text-xl
                    group-hover:text-[#B8893A] transition"
                    >
                      {f.title}
                    </h3>

                    <div className="text-black/40 group-hover:text-[#B8893A] transition mt-1">
                      {f.icon}
                    </div>
                  </div>

                  <div className="h-px bg-black/10 my-5 sm:my-6"></div>

                  <p
                    className="text-gray-500"
                    style={{
                      fontSize: "clamp(14px, 1.2vw, 16px)",
                      lineHeight: "1.6",
                    }}
                  >
                    {f.description}
                  </p>

                  <div className="mt-6 text-[#B8893A] text-sm font-semibold">
                    Learn More →
                  </div>
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Background Glow */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#B8893A]/5 blur-3xl rounded-full -z-10"></div>
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#B8893A]/5 blur-3xl rounded-full -z-10"></div>
    </section>
  );
}